import { Grid } from '../core/Grid';
import { Cell, TRANSPARENT } from '../core/types';
import { CellRun, encodeComponentRuns } from '../project/Serializer';
import { read, storage, write as writeKey } from '../state/storage';
import type { ExprValue } from '../runtime/Expressions';
import type { Align, ColorRule, VariableDef, VariableType } from '../runtime/Variables';

/**
 * Templates the user saves.
 *
 * Stored as run-length encoded cells (the same encoding the project format
 * uses) in localStorage, so a saved template keeps its colours and attributes
 * rather than degrading to plain text. Everything read back is validated the
 * same way a project file is — localStorage is user-writable, so it is input.
 *
 * Public templates pass through the same sanitizer after the server returns
 * them. The server validates too; doing it again on this side means a compromised
 * or buggy endpoint cannot put control characters or malformed variable
 * definitions into the editor.
 */

export interface UserTemplate {
  id: string;
  name: string;
  description: string;
  width: number;
  height: number;
  createdAt: string;
  runs: CellRun[];
  variables: VariableDef[];
}

const KEY = 'templates';
const MAX_TEMPLATES = 200;
const MAX_CELLS = 400_000;
const MAX_RUNS = MAX_CELLS;
const MAX_VARIABLES = 200;

const UNSAFE_TEXT =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u202A-\u202E\u2028\u2029\u2066-\u2069\uFEFF]/u;
const UNSAFE_CELL =
  /[\u0000-\u001F\u007F-\u009F\u202A-\u202E\u2028\u2029\u2066-\u2069\uFEFF]/u;
const VARIABLE_TYPES: ReadonlySet<string> = new Set([
  'string',
  'integer',
  'float',
  'boolean',
  'percentage',
  'duration',
  'timestamp',
  'array',
  'computed',
]);
const ALIGNS: ReadonlySet<string> = new Set(['left', 'center', 'right']);
const COLOR_RULE = /^#[0-9a-fA-F]{3,8}$/;

export function listUserTemplates(): UserTemplate[] {
  try {
    const raw = read(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(sanitizeUserTemplate)
      .filter((t): t is UserTemplate => t !== null);
  } catch {
    return [];
  }
}

/**
 * Validates a template-shaped value. Local files and the public API both go
 * through here, so every field that reaches the template grid or the variable
 * manager has been bounded first.
 */
export function sanitizeUserTemplate(raw: unknown): UserTemplate | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const t = raw as Record<string, unknown>;
  const width = clamp(t.width, 1, 2000);
  const height = clamp(t.height, 1, 2000);
  if (!width || !height || width * height > MAX_CELLS) return null;
  if (!Array.isArray(t.runs)) return null;

  return {
    id: safeId(t.id),
    name: safeText(t.name, 80) || 'Untitled template',
    description: safeText(t.description, 200),
    width,
    height,
    createdAt: safeDate(t.createdAt),
    runs: sanitizeRuns(t.runs, width * height),
    variables: sanitizeTemplateVariables(t.variables),
  };
}

function sanitizeRuns(raw: unknown[], cells: number): CellRun[] {
  const out: CellRun[] = [];
  let total = 0;
  for (const entry of raw) {
    if (out.length >= MAX_RUNS || total >= cells) break;
    if (!Array.isArray(entry) || entry.length < 5) continue;
    const count = Math.trunc(Number(entry[0]) || 0);
    if (!Number.isFinite(count) || count < 1) continue;
    const used = Math.min(count, cells - total);
    const char =
      typeof entry[1] === 'string' ? entry[1].replace(UNSAFE_CELL, '').slice(0, 8) : '';
    out.push([
      used,
      char,
      templateColor(entry[2]),
      templateColor(entry[3]),
      Math.max(0, Math.min(0xffff, Math.trunc(Number(entry[4]) || 0))),
    ]);
    total += used;
  }
  return out;
}

function clamp(v: unknown, min: number, max: number): number {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : 0;
}

function safeText(v: unknown, max: number): string {
  if (typeof v !== 'string') return '';
  const text = v
    .normalize('NFC')
    .replace(UNSAFE_TEXT, '')
    .replace(/\s+/g, ' ')
    .trim();
  return Array.from(text).slice(0, max).join('');
}

function safeExpression(v: unknown): string {
  if (typeof v !== 'string') return '';
  return v.replace(UNSAFE_TEXT, '').slice(0, 500).trim();
}

function safeId(v: unknown): string {
  if (typeof v === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(v)) return v;
  return `tpl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function safeDate(v: unknown): string {
  if (typeof v === 'string' && v.length <= 40 && Number.isFinite(Date.parse(v))) {
    return new Date(v).toISOString();
  }
  return new Date().toISOString();
}

function readScalar(v: unknown): ExprValue | undefined {
  if (v === null || typeof v === 'boolean') return v;
  if (typeof v === 'string') return safeText(v, 500);
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return undefined;
}

function readColorRules(v: unknown): ColorRule[] {
  if (!Array.isArray(v)) return [];
  const out: ColorRule[] = [];
  for (const raw of v.slice(0, 32)) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) continue;
    const rule = raw as Record<string, unknown>;
    const color = typeof rule.color === 'string' ? rule.color.trim() : '';
    if (!COLOR_RULE.test(color)) continue;
    const next: ColorRule = { color };
    if (typeof rule.min === 'number' && Number.isFinite(rule.min)) next.min = rule.min;
    if (typeof rule.max === 'number' && Number.isFinite(rule.max)) next.max = rule.max;
    if (typeof rule.equals === 'string') next.equals = safeText(rule.equals, 64);
    out.push(next);
  }
  return out;
}

export function sanitizeTemplateVariables(raw: unknown): VariableDef[] {
  if (!Array.isArray(raw)) return [];
  const out: VariableDef[] = [];
  const seen = new Set<string>();
  for (const item of raw.slice(0, MAX_VARIABLES)) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) continue;
    const v = item as Record<string, unknown>;
    const name = safeText(v.name, 64);
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name) || seen.has(name)) continue;
    if (typeof v.type !== 'string' || !VARIABLE_TYPES.has(v.type)) continue;
    seen.add(name);

    const def: VariableDef = { name, type: v.type as VariableType };
    const description = safeText(v.description, 240);
    if (description) def.description = description;

    for (const key of ['defaultValue', 'previewValue', 'fallback'] as const) {
      const scalar = readScalar(v[key]);
      if (scalar !== undefined) def[key] = scalar;
    }

    if (def.type === 'computed') {
      const expression = safeExpression(v.expression);
      if (expression) def.expression = expression;
    }

    if (v.width !== undefined) {
      const width = Math.trunc(Number(v.width));
      if (Number.isFinite(width) && width >= 0 && width <= 500) def.width = width;
    }

    if (typeof v.align === 'string' && ALIGNS.has(v.align)) {
      def.align = v.align as Align;
    }

    if (typeof v.padChar === 'string' && v.padChar.length > 0) {
      const pad = v.padChar.replace(UNSAFE_CELL, '').slice(0, 2);
      if (pad) def.padChar = pad;
    }

    if (typeof v.format === 'string') {
      const format = safeText(v.format, 200);
      if (format) def.format = format;
    }

    const colorRules = readColorRules(v.colorRules);
    if (colorRules.length > 0) def.colorRules = colorRules;

    out.push(def);
  }
  return out;
}

function write(templates: UserTemplate[]): boolean {
  if (!storage()) return false;
  return writeKey(KEY, JSON.stringify(templates.slice(0, MAX_TEMPLATES)));
}

export interface SaveTemplateInput {
  name: string;
  description?: string;
  grid: Grid;
  variables?: readonly VariableDef[];
}

/** Saves a grid as a template, trimmed to its painted content. */
export function saveUserTemplate(input: SaveTemplateInput): UserTemplate | null {
  const bounds = input.grid.contentBounds();
  const trimmed = bounds ? input.grid.extract(bounds) : input.grid;
  const text = trimmed.toText();
  const variables = sanitizeTemplateVariables(input.variables ?? []).filter((v) =>
    text.includes(`{{${v.name}`),
  );
  const template: UserTemplate = {
    id: `tpl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    name: safeText(input.name, 80) || 'Untitled template',
    description: safeText(input.description, 200),
    width: trimmed.width,
    height: trimmed.height,
    createdAt: new Date().toISOString(),
    runs: encodeComponentRuns((i) => trimmed.getAt(i), trimmed.length),
    // Only the variables the template actually references travel with it.
    variables,
  };
  const all = [template, ...listUserTemplates()];
  return write(all) ? template : null;
}

export function removeUserTemplate(id: string): void {
  write(listUserTemplates().filter((t) => t.id !== id));
}

export function renameUserTemplate(id: string, name: string): void {
  write(
    listUserTemplates().map((t) =>
      t.id === id ? { ...t, name: safeText(name, 80) || 'Untitled template' } : t,
    ),
  );
}

/** Rebuilds a template's cells. */
export function templateGrid(template: UserTemplate): Grid {
  const grid = new Grid(template.width, template.height);
  let i = 0;
  for (const run of template.runs) {
    if (!Array.isArray(run) || run.length < 5) continue;
    const count = Math.max(0, Math.min(grid.length - i, Math.trunc(Number(run[0]) || 0)));
    const cell: Cell = {
      char: typeof run[1] === 'string' ? run[1].replace(UNSAFE_CELL, '').slice(0, 8) : '',
      fg: templateColor(run[2]),
      bg: templateColor(run[3]),
      attrs: Math.max(0, Math.min(0xffff, Math.trunc(Number(run[4]) || 0))),
    };
    for (let n = 0; n < count; n++) grid.setAt(i + n, cell);
    i += count;
    if (i >= grid.length) break;
  }
  return grid;
}

function templateColor(v: unknown): number {
  const n = Math.trunc(Number(v));
  if (!Number.isFinite(n) || n < 0) return TRANSPARENT;
  return n & 0xffffff;
}

/** Plain-text thumbnail for the template browser. */
export function templatePreview(template: UserTemplate, maxRows = 12): string {
  return templateGrid(template)
    .toText()
    .split('\n')
    .slice(0, maxRows)
    .join('\n');
}

export function exportUserTemplates(): string {
  return JSON.stringify(
    { format: 'tui-templates', version: 1, templates: listUserTemplates() },
    null,
    2,
  );
}

/** Merges templates from a file, keeping existing ones. Returns how many. */
export function importUserTemplates(json: string): number {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return 0;
  }
  if (typeof parsed !== 'object' || parsed === null) return 0;
  const list = (parsed as Record<string, unknown>).templates;
  if (!Array.isArray(list)) return 0;
  const incoming = list
    .map(sanitizeUserTemplate)
    .filter((t): t is UserTemplate => t !== null);
  if (incoming.length === 0) return 0;
  const existing = listUserTemplates();
  const byId = new Set(existing.map((t) => t.id));
  const merged = [...incoming.filter((t) => !byId.has(t.id)), ...existing];
  return write(merged) ? incoming.length : 0;
}
